import { createFileRoute } from '@tanstack/react-router';
import { useRef, useState } from 'react';
import { Seo } from '../$portalId/-components/seo';
import { useLingui } from '@lingui/react/macro';
import { Layout, Title } from './-components/layout';
import { Header } from './-components/header';
import { Footer } from './-components/footer';
import { useAppForm } from '@worldcoo/ui/components/form';
import * as z from 'zod';
import {
	Dialog,
	DialogClose,
	DialogContent,
	DialogFooter,
	DialogHeader,
	DialogTitle,
	DialogTrigger,
} from '@worldcoo/ui/components/dialog';
import { Button } from '@worldcoo/ui/components/button';
import { Gradient } from '@worldcoo/ui/custom/gradient';
import { TermsAndConditionsDialog } from '../-components/terms-and-conditions';
import { IsScrolledToBottom } from '../-components/is-scrolled-to-bottom';
import { useTranslation } from '@/lib/i18n';
import { useMutation } from '@tanstack/react-query';
import { postContactFormOptions } from '@/lib/queries';
import { generateCaptchaToken } from '@worldcoo/ui/lib/captcha';
import type { ContactForm } from '@worldcoo/monolith/portals';
import { toast } from 'sonner';

export const Route = createFileRoute('/www/contact')({
	component: Component,
});

function Component() {
	const { t } = useLingui();
	const { i18n, language } = useTranslation();
	const { mutateAsync: postContactForm, isSuccess } = useMutation(postContactFormOptions());

	// We need to match approximattely Slack's block limit
	const contactSchema = z.object({
		name: z
			.string()
			.min(1, t`Name is required`)
			.max(100, t`Input is too long, maximum 100 characters allowed`),
		email: z.email(t`Invalid email`).max(100, t`Input is too long, maximum 100 characters allowed`),
		phone: z.string().max(100, t`Input is too long, maximum 100 characters allowed`),
		category: z.union(
			[
				z.literal('merchant'),
				z.literal('ngo'),
				z.literal('donor'),
				z.literal('press'),
				z.literal('newsletter'),
				z.literal('other'),
			],
			t`Subject is required`,
		),
		message: z
			.string()
			.min(1, t`Message is required`)
			.max(2500, t`Message is too long, maximum 2500 characters allowed`),
		checkbox: z.object(
			{
				type: z.literal('termsAndConditions'),
				checked: z.boolean().refine((v) => v, { message: t`You must accept the data protection terms` }),
				text: z.string(),
				label: z.string(),
			},
			t`You must accept the data protection terms`,
		),
	});

	const form = useAppForm({
		defaultValues: {
			name: '',
			email: '',
			phone: '',
			category: '' as ContactForm['category'],
			message: '',
			checkbox: {
				type: 'termsAndConditions' as const,
				checked: false,
				text: '',
				label: '',
			},
		},
		validators: { onChange: contactSchema },
		onSubmit: async ({ value }) => {
			const captchaToken = await generateCaptchaToken();
			const result = await postContactForm({ ...value, captchaToken, language });
			if (!result.captchaPassed) {
				toast.error(t`Captcha verification failed. Please try again.`);
			} else if (!result.success) {
				toast.error(t`An error occurred while sending your message. Please try again later.`);
			} else {
				toast.success(t`Your message has been sent successfully. We will get back to you as soon as possible.`);
			}
		},
	});

	const [openTermsDialog, setOpenTermsDialog] = useState(false);

	const termsLabelRef = useRef<HTMLDivElement>(null);
	const termsDialogRef = useRef<HTMLDivElement>(null);
	const termsTextRef = useRef<HTMLTableElement>(null);
	const hiddenTermsTextRef = useRef<HTMLTableElement>(null);

	return (
		<Layout>
			<Header />
			<Seo
				title={t`Contact - Worldcoo`}
				description={t`If you'd like to incorporate Charity Rounding Up, if you're a social entity, or if you'd like some more information, please contact us.`}
			/>

			{/* Hero & Form Section */}
			<section className="bg-hero py-16 md:py-24">
				<div className="mx-auto flex max-w-6xl flex-col gap-12 px-8">
					<Title>
						{t`If you'd like to incorporate Charity Rounding Up, if you're a social entity, or if you'd like some more information, please contact us.`}
					</Title>

					<div className="grid gap-12 lg:grid-cols-2">
						{/* Contact Form */}
						<div className="bg-background shadow-3xl rounded-lg p-8">
							<form
								onSubmit={(e) => {
									e.preventDefault();
									e.stopPropagation();
									form.handleSubmit();
								}}
								className="flex flex-col gap-6"
							>
								<form.AppField name="name" children={(field) => <field.TextField label={t`Name*`} />} />
								<form.AppField name="email" children={(field) => <field.TextField label={t`Email*`} type="email" />} />
								<form.AppField name="phone" children={(field) => <field.TextField label={t`Phone`} type="tel" />} />

								<form.AppField
									name="category"
									children={(field) => (
										<field.SelectTextField
											label={t`Subject*`}
											options={[
												{ value: 'merchant', label: t`I am a merchant and I want to activate Charity Rounding Up` },
												{ value: 'ngo', label: t`I am an NGO and I want to raise funds` },
												{ value: 'donor', label: t`I am a donor and I have a query` },
												{ value: 'press', label: t`Press` },
												{ value: 'other', label: t`Other` },
											]}
										/>
									)}
								/>

								<form.AppField
									name="message"
									children={(field) => <field.TextareaField label={t`Message*`} rows={5} />}
								/>

								<form.AppField
									name="checkbox.checked"
									listeners={{
										onChange: ({ value }) => {
											// When checkbox is toggled directly, capture text and label
											const textRef = termsTextRef.current || hiddenTermsTextRef.current;
											if (value && termsLabelRef.current && textRef) {
												form.setFieldValue('checkbox.label', termsLabelRef.current.innerHTML);
												form.setFieldValue('checkbox.text', textRef.innerHTML);
											} else if (!value) {
												form.setFieldValue('checkbox.label', '');
												form.setFieldValue('checkbox.text', '');
											}
										},
									}}
									children={(field) => (
										<field.CheckboxField
											className="gap-3"
											label={
												<div ref={termsLabelRef} className="font-normal">
													{t`I have read, understand and accept the `}
													<Dialog open={openTermsDialog} onOpenChange={setOpenTermsDialog}>
														<DialogTrigger className="text-primary contents cursor-pointer">
															{t`data protection terms`}
														</DialogTrigger>
														<DialogContent
															ref={termsDialogRef}
															aria-describedby={undefined}
															// Changed grid by flex so the text overflows correctly in small screens
															className="scrollbar flex max-h-dvh flex-col gap-6 overflow-y-auto sm:max-w-xl"
														>
															<DialogHeader>
																<DialogTitle className="capitalize">{i18n.termsAndConditions.title}</DialogTitle>
															</DialogHeader>

															<div className="unreset-tailwind">
																<TermsAndConditionsDialog
																	ref={termsTextRef}
																	responsible={i18n.termsAndConditions.responsible.description}
																	purpose={i18n.termsAndConditions.purpose.description}
																	legitimacy={i18n.termsAndConditions.legitimacy.description}
																	recipients={i18n.termsAndConditions.recipients.description}
																	rights={i18n.termsAndConditions.rights.description}
																	additionalInfo={i18n.termsAndConditions.additionalInfo.description}
																/>
															</div>

															<div className="sticky bottom-0 pt-12">
																<Gradient className="absolute -bottom-6 -z-10 h-[180px]" />
																<DialogFooter>
																	<DialogClose asChild>
																		<IsScrolledToBottom dialogRef={termsDialogRef}>
																			{(scrolledToBottom) => (
																				<Button
																					type="button"
																					size="xl"
																					className="mx-auto"
																					style={{ transition: 'all 0.15s ease-in-out' }}
																					disabled={!scrolledToBottom}
																					onClick={() => {
																						if (!termsTextRef.current || !termsLabelRef.current) return;

																						form.setFieldValue('checkbox.checked', true);
																						form.setFieldValue('checkbox.label', termsLabelRef.current.innerHTML);
																						form.setFieldValue('checkbox.text', termsTextRef.current.innerHTML);

																						setOpenTermsDialog(false);
																					}}
																				>
																					{i18n.termsAndConditions.accept}
																				</Button>
																			)}
																		</IsScrolledToBottom>
																	</DialogClose>
																</DialogFooter>
															</div>
														</DialogContent>
													</Dialog>
												</div>
											}
										/>
									)}
								/>

								<div className="flex items-center justify-between">
									<p className="text-muted-foreground text-sm">{t`*Required field`}</p>
									<form.AppForm>
										<form.SubmitButton label={t`Submit`} disabled={isSuccess} />
									</form.AppForm>
								</div>

								{/* Hidden terms content for capturing refs when checkbox is toggled directly */}
								<div className="hidden">
									<TermsAndConditionsDialog
										ref={hiddenTermsTextRef}
										responsible={i18n.termsAndConditions.responsible.description}
										purpose={i18n.termsAndConditions.purpose.description}
										legitimacy={i18n.termsAndConditions.legitimacy.description}
										recipients={i18n.termsAndConditions.recipients.description}
										rights={i18n.termsAndConditions.rights.description}
										additionalInfo={i18n.termsAndConditions.additionalInfo.description}
									/>
								</div>
							</form>
						</div>

						{/* Contact Info & Press */}
						<div className="flex flex-col gap-3">
							{/* Contact Info */}
							<div className="p-8">
								<h2 className="mb-4 text-xl font-bold">{t`Contact details`}</h2>
								<ul className="flex flex-col gap-2">
									<li>
										<a href="tel:+34936110104" className="hover:text-primary transition-colors">
											T. (+34) 936 11 01 04
										</a>
									</li>
									<li>
										<a
											href="https://goo.gl/maps/5DDj97UEuZTjRb9P9"
											target="_blank"
											rel="noopener noreferrer"
											className="hover:text-primary transition-colors"
										>
											Carrer de Llull, 108, Sant Martí, 08005 Barcelona
										</a>
									</li>
								</ul>
							</div>
						</div>
					</div>
				</div>
			</section>

			<Footer />
		</Layout>
	);
}
